import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, useTheme, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuditContextBar from "../Components/AuditContextBar";
import ControlsCategoriesView from "../Components/ControlsCategoriesView";
import ControlsHeader from "../Components/ControlsHeader";
import ControlResourcesDrawer from "../Components/ControlResourcesDrawer";
import ErrorUI from "../Components/ErrorUI";
import {
  getComplianceControls,
  getComplianceControlSections,
  clearAllComplianceMessages,
  clearComplianceControls,
} from "../../../store/compliance/complianceSlice";

const ControlsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [selectedSection, setSelectedSection] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState(null);
  const [selectedResourcesUri, setSelectedResourcesUri] = useState(null);
  const [selectedAvailableTypes, setSelectedAvailableTypes] = useState([]);
  const [selectedAvailableRegions, setSelectedAvailableRegions] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const location = useLocation();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const compliance = searchParams.get("compliance");
  const runId = searchParams.get("runId");
  const auditId = searchParams.get("auditId");
  const accountNumber = searchParams.get("accountNumber") || localStorage.getItem("selectedAccountNumber");

  // Redux selectors
  const {
    complianceControlSections,
    isGettingComplianceControlSections,
    getComplianceControlSectionsError,
    complianceControls,
    isGettingComplianceControls,
    getComplianceControlsError,
  } = useSelector((state) => state.compliance);

  // Local storage
  const scanDate = localStorage.getItem("selectedScanDate");
  const compliancePercent = localStorage.getItem("selectedCompliancePercent");

  // Fetch sections on mount or param change
  useEffect(() => {
    dispatch(clearComplianceControls());
    dispatch(clearAllComplianceMessages());
    setSelectedSection(null);
    if (compliance && runId) {
      dispatch(getComplianceControlSections({ compliance, runId }));
    }
  }, [compliance, runId, dispatch]);

  // Fetch controls for selected section
  useEffect(() => {
    if (selectedSection) {
      dispatch(getComplianceControls({ compliance, runId, section: selectedSection }));
    }
  }, [selectedSection, compliance, runId, dispatch]);

  // Handler for section selection
  const handleSectionSelect = (section) => {
    console.log("🚀 ~ handleSectionSelect ~ section:", section)
    setSelectedSection(section);
  };

  // Handler for resource drawer
  const handleResourceClick = (controlId, resourcesUri, row) => {
    setSelectedControlId(controlId);
    setSelectedResourcesUri(resourcesUri);
    setSelectedAvailableTypes(row.AvailableTypes || []);
    setSelectedAvailableRegions(row.AvailableRegions || []);
    setSelectedRunId(runId);
    setDrawerOpen(true);
  };

  return (
    <Box sx={{ 
      padding: isMobile ? "1rem" : isTablet ? "1.5rem" : "1.5rem 2rem", 
      mb: "3rem", 
      backgroundColor: "#F9F9F9", 
      minHeight: "100vh", 
      display: "flex", 
      flexDirection: "column", 
      gap: 2,
      overflow: 'hidden', // Prevent horizontal scroll
      maxWidth: '100vw', // Ensure it doesn't exceed viewport width
    }}>
      {auditId ? <AuditContextBar auditId={auditId} /> : null}
      {!compliance || !runId ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh" width="100%">
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Missing required parameters. Please select a compliance framework from the dashboard.
          </Typography>
        </Box>
      ) : isGettingComplianceControlSections ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <CircularProgress color="primary" />
        </Box>
      ) : getComplianceControlSectionsError ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Box width="100%">
            <ErrorUI
              error={getComplianceControlSectionsError}
              onRetry={() => dispatch(getComplianceControlSections({ compliance, runId }))}
            />
          </Box>
        </Box>
      ) : !complianceControlSections ||
        !Array.isArray(complianceControlSections.controlsListJson) ||
        complianceControlSections.controlsListJson.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Typography variant="h6" color="text.secondary">
            No controls data available
          </Typography>
        </Box>
      ) : (
        <>
          <ControlsHeader
            controls={{
              controlsListJson: complianceControlSections.controlsListJson,
              trendScore: complianceControlSections.trendScore,
              totalResources: complianceControlSections.totalResources,
              passedResources: complianceControlSections.passedResources,
              failedResources: complianceControlSections.failedResources,
            }}
            compliance={compliance}
            scanDate={scanDate}
            compliancePercent={compliancePercent}
            runId={runId}
            accountNumber={accountNumber}
          />
          <Box sx={{ 
            flex: 1, 
            display: "flex", 
            flexDirection: "column", 
            minHeight: 0,
            overflow: 'hidden', // Prevent overflow
          }}>
            <ControlsCategoriesView
              controls={complianceControlSections.controlsListJson}
              onSectionSelect={handleSectionSelect}
              selectedSection={selectedSection}
              sectionControls={complianceControls}
              isLoadingControls={isGettingComplianceControls}
              controlsError={getComplianceControlsError}
              onResourceClick={handleResourceClick}
              compliance={compliance}
              runId={runId}
              accountNumber={accountNumber}
            />
          </Box>
          <ControlResourcesDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            controlId={selectedControlId}
            resourcesUri={selectedResourcesUri}
            availableTypes={selectedAvailableTypes}
            availableRegions={selectedAvailableRegions}
            runId={selectedRunId}
          />
        </>
      )}
    </Box>
  );
};

export default ControlsPage;
